import { ArrowType, LineType } from '../apiTypes';
import {
  Complaint,
  TransferConfig,
  TransferEffectSummaries,
} from './integrationApiTypes';

export type ScheduleMeta = {
  meta: {
    firstCompleted: string | null;
    lastCompleted: string | null;
    nextStart: string;
    paused: boolean;
    lastResult: {
      jobId: string;
      status: 'error' | 'warn' | 'success';
      message: string | null;
      summary: null | ScheduleSummary;
    } | null;
  };
};

export interface BaseScheduleOptions {
  type: string;
  name: string;
  managesWorkspace: boolean;
  tabularMapping: TransferConfig;
  sourceId: null | string;
  runAs: RunAs;
}
/**
 * @deprecated Use Cron based scheduling
 */
type DailySchedulingPolicy = {
  type: 'nightly';
};

/**
 * @deprecated Use Cron based scheduling
 */
export type WeeklySchedulingPolicy = {
  type: 'weekly';
  weekDay:
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday';
};

export type CronHour =
  | '17'
  | '18'
  | '19'
  | '20'
  | '21'
  | '22'
  | '23'
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5';

// Day of Week (0-6)
export type CronDayOfWeek = '0' | '1' | '2' | '3' | '4' | '5' | '6';

/**
 * Use Cases
 */
export const DEFAULT_NIGHTLY_CRON = '0 0 * * *' as const;
type NightlyCron = `0 ${CronHour} * * *`;

export const DEFAULT_WEEKLY_CRON_PREFIX = '0 1 * * ' as const;

// Weekly schedule at any valid hour on a specific day
type WeeklyCron = `0 ${CronHour} * * ${CronDayOfWeek}`;

export type CronSchedulingPolicy = {
  type: 'cron';
  cronExpression: NightlyCron | WeeklyCron;
};

export type SchedulingPolicy =
  | DailySchedulingPolicy
  | WeeklySchedulingPolicy
  | CronSchedulingPolicy;

export type TypeCounts = {
  existing: number;
  created: number;
  updated: number;
  deleted: number;
};

export type ComponentTypeStyle = {
  color: string;
  icon: string;
  shape: string | null;
  image: string | null;
};

export type ComponentTypeInfo = {
  name: string | null;
  counts: TypeCounts;
  style?: ComponentTypeStyle;
};

export type WorkspaceComponentTypes = {
  workspace: {
    id: string | null;
    name: string;
  };
  types: ComponentTypeInfo[];
};

export type ReferenceTripleStyle = {
  color: string | null;
  lineEnding: ArrowType;
  svgStyle: string | null;
  lineBeginning: ArrowType | null;
  line: LineType;
};

export type ReferenceTriple = {
  referenceType: string;
  sourceWorkspace: {
    id: string | null;
    name: string;
  };
  targetWorkspace: {
    id: string | null;
    name: string;
  };
  sourceComponentType: string;
  targetComponentType: string;
  counts: TypeCounts;
  style?: ReferenceTripleStyle;
};

type ScheduleSummary = TransferEffectSummaries & {
  validation: Complaint[];
  warnings: number;
  typesSummary?: {
    componentTypes: WorkspaceComponentTypes[];
    triples: ReferenceTriple[];
  };
};

export type RunAs = {
  type: 'user' | 'service-account';
  id: string;
};

export type ScheduleFormData = {
  name: string;
  policy: SchedulingPolicy;
  managesWorkspace: boolean;
  runAs: RunAs;
};

export interface BaseJobPayload {
  _id: string;
  schedulingPolicy: SchedulingPolicy;
}

export enum ScheduleType {
  ARDOQ_IMPORT = 'ardoq-import',
  ARDOQ_REFERENCE_DATA_IMPORT = 'ardoq-reference-data-import',
  ARDOQ_SAMPLE_DATA_IMPORT = 'ardoq-sample-data-import',
  SHAREPOINT_FILES_IMPORT = 'sharepoint-files-import',
  SERVICENOW_IMPORT = 'servicenow-import',
  SERVICENOW_EXPORT = 'servicenow-export',
  ITPEDIA_ENRICHMENT = 'itpedia-enrichment',
  CLOUD_IMPORT = 'cloud-provider-import',
  JIRA_IMPORT = 'jira-software-import',
  SIGNAVIO_IMPORT = 'signavio-import',
  SIGNAVIO_EXPORT = 'signavio-export',
  CELONIS_IMPORT = 'celonis-import',
  SYMBIO_IMPORT = 'symbio-import',
  MICROSOFT_ENTRA_ID_UNIFIED_IMPORT = 'entra-id-import',

  // TODO: huge discrepency with namings in CELONIS_EXPORT
  // BE is implemented with `symbio-exporter`
  // FE is implemented with the `celonis-exporter`
  CELONIS_EXPORT = 'symbio-export',
  IMPORT_BUILDER = 'import-builder-import',
  AZURE_RESOURCES_IMPORT = 'azure-resources-import',
}
